import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import {
  Wrench, Search, Loader2, AlertTriangle, Trash2, Pencil, CheckSquare, Square,
  UserCog, ShieldAlert, Clock, Users, X, Download,
} from "lucide-react";

const ROLE_LABEL = {
  lawyer: "Abogado/a",
  mediator: "Mediador/a",
  requester: "Solicitante",
  admin: "Admin",
};

export default function AdminUsersMaintenance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ role: "", filter: "all", q: "", inactive_days: 90 });
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [flash, setFlash] = useState({ type: "", text: "" });
  const [exporting, setExporting] = useState("");

  const doExport = async (scope) => {
    setExporting(scope);
    try {
      const params = new URLSearchParams();
      params.set("scope", scope);
      if (scope === "filtered") {
        if (filters.role) params.set("role", filters.role);
        if (filters.filter) params.set("filter", filters.filter);
        if (filters.q) params.set("q", filters.q);
        if (filters.inactive_days) params.set("inactive_days", filters.inactive_days);
      }
      const resp = await api.get(`/admin/users/export?${params.toString()}`, { responseType: "blob" });
      const count = resp.headers?.["x-exported-count"] || "";
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `colegalink_users_${scope}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setFlash({ type: "success", text: `CSV descargado (${count || "?"} registros).` });
    } catch (err) {
      // Cuando la respuesta es blob, el detail viene como Blob
      let msg = err.message;
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) {
          const j = JSON.parse(txt);
          msg = formatApiError(j.detail) || msg;
        }
      } catch { /* ignore */ }
      setFlash({ type: "error", text: msg });
    } finally {
      setExporting("");
      setTimeout(() => setFlash({ type: "", text: "" }), 6000);
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filters.role) params.set("role", filters.role);
      if (filters.filter) params.set("filter", filters.filter);
      if (filters.q) params.set("q", filters.q);
      if (filters.inactive_days) params.set("inactive_days", filters.inactive_days);
      const { data } = await api.get(`/admin/users-maintenance?${params.toString()}`);
      setItems(data.items || []);
      setSelected(new Set()); // reset selección al recargar
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const openConfirm = () => {
    if (selected.size === 0) return;
    setReason("");
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/users-bulk-delete", {
        user_ids: Array.from(selected),
        confirm: true,
        reason: reason.trim() || null,
      });
      setFlash({ type: "success", text: `Se eliminaron ${data.deleted_count} usuario(s).` });
      setConfirmOpen(false);
      setSelected(new Set());
      await load();
    } catch (err) {
      setFlash({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setDeleting(false);
      setTimeout(() => setFlash({ type: "", text: "" }), 6000);
    }
  };

  const stats = {
    total: items.length,
    incomplete: items.filter(i => i.incomplete).length,
    inactive: items.filter(i => i.inactive).length,
    unverified: items.filter(i => i.unverified_pro).length,
    noBank: items.filter(i => (i.role === "lawyer" || i.role === "mediator") && !i.banking_info_complete).length,
  };

  return (
    <div className="container-app py-8" data-testid="admin-mantenimiento-page">
      <AdminNav/>

      <div className="flex items-start gap-3">
        <Wrench size={28} className="text-amber-300 mt-1"/>
        <div>
          <h1 className="serif text-3xl">Mantenimiento de Usuarios</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Herramientas de calidad de datos. Detecta perfiles incompletos, cuentas inactivas y
            realiza eliminaciones masivas con confirmación. Cada acción queda en el audit log (Ley 19.628).
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="mant-stats">
        <StatCard icon={<Users size={16}/>} label="Total en vista" value={stats.total} color="text-slate-200"/>
        <StatCard icon={<AlertTriangle size={16}/>} label="Perfiles incompletos" value={stats.incomplete} color="text-amber-300"/>
        <StatCard icon={<Clock size={16}/>} label="Inactivos (90d)" value={stats.inactive} color="text-orange-300"/>
        <StatCard icon={<ShieldAlert size={16}/>} label="Sin verificar" value={stats.unverified} color="text-red-300"/>
        <StatCard icon={<UserCog size={16}/>} label="Sin datos bancarios" value={stats.noBank} color="text-cyan-300"/>
      </div>

      {/* Filtros */}
      <div className="panel p-4 mt-6 flex flex-wrap gap-3 items-end" data-testid="mant-filters">
        <div className="grow min-w-[220px]">
          <label className="text-xs label-eyebrow">Buscar</label>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-3 text-muted-foreground"/>
            <input
              className="field pl-9 mt-1 w-full"
              placeholder="Nombre, email o RUT…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="mant-search"
            />
          </div>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Rol</label>
          <select className="field mt-1" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="mant-role">
            <option value="">Todos</option>
            <option value="lawyer">Abogado/a</option>
            <option value="mediator">Mediador/a</option>
            <option value="requester">Solicitante</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Filtro calidad</label>
          <select className="field mt-1" value={filters.filter} onChange={(e) => setFilters({ ...filters, filter: e.target.value })} data-testid="mant-filter">
            <option value="all">Todos</option>
            <option value="incomplete">Perfiles incompletos</option>
            <option value="inactive">Inactivos</option>
            <option value="unverified">Sin verificar</option>
          </select>
        </div>
        {filters.filter === "inactive" && (
          <div>
            <label className="text-xs label-eyebrow">Días sin login</label>
            <input type="number" min="1" max="3650" className="field mt-1 w-24" value={filters.inactive_days}
              onChange={(e) => setFilters({ ...filters, inactive_days: parseInt(e.target.value, 10) || 90 })}
              data-testid="mant-inactive-days"/>
          </div>
        )}
      </div>

      {/* Toolbar bulk actions */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {selected.size > 0 ? (
            <span className="text-amber-200">{selected.size} seleccionado{selected.size === 1 ? "" : "s"}</span>
          ) : (
            <>Selecciona uno o más usuarios para eliminarlos</>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => doExport("filtered")}
            disabled={!!exporting}
            className="btn-outline text-sm inline-flex items-center gap-1.5 disabled:opacity-40 border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
            title="Exporta solo los usuarios que cumplen los filtros actuales"
            data-testid="mant-export-filtered"
          >
            {exporting === "filtered" ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
            Exportar filtrados
          </button>
          <button
            onClick={() => doExport("all")}
            disabled={!!exporting}
            className="btn-outline text-sm inline-flex items-center gap-1.5 disabled:opacity-40 border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10"
            title="Descarga TODOS los usuarios de la base (respaldo completo)"
            data-testid="mant-export-all"
          >
            {exporting === "all" ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
            Respaldo total (CSV)
          </button>
          <button
            onClick={openConfirm}
            disabled={selected.size === 0}
            className="btn-outline text-sm inline-flex items-center gap-1.5 disabled:opacity-40 border-red-400/40 text-red-300 hover:bg-red-400/10"
            data-testid="mant-bulk-delete"
          >
            <Trash2 size={13}/> Eliminar seleccionados
          </button>
        </div>
      </div>

      {flash.text && (
        <div className={`mt-3 p-3 rounded-md text-sm ${flash.type === "success" ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border border-red-500/40 bg-red-500/10 text-red-200"}`} data-testid="mant-flash">
          {flash.text}
        </div>
      )}
      {error && (
        <div className="mt-3 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="mant-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {/* Tabla */}
      <div className="mt-4 panel p-0 overflow-x-auto" data-testid="mant-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={16} className="animate-spin"/> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="mant-empty">
            No hay usuarios con estos filtros.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 w-10">
                  <button onClick={toggleAll} className="text-muted-foreground hover:text-foreground" data-testid="mant-select-all">
                    {selected.size === items.length && items.length > 0 ? <CheckSquare size={15}/> : <Square size={15}/>}
                  </button>
                </th>
                <th className="text-left px-3 py-2">Usuario</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Rol</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Contacto</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Último login</th>
                <th className="text-left px-3 py-2">Flags</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`mant-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleOne(u.id)} className="text-muted-foreground hover:text-foreground" data-testid={`mant-check-${u.id}`}>
                      {selected.has(u.id) ? <CheckSquare size={15} className="text-amber-300"/> : <Square size={15}/>}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-sm">{u.name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs">
                    <span className="pill text-[10px] border-white/10 bg-white/5">{ROLE_LABEL[u.role] || u.role}</span>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">
                    <div>{u.phone || "—"}</div>
                    <div>{u.region || "—"} · {u.comuna || "—"}</div>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("es-CL") : <span className="italic">nunca</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.incomplete && (
                        <span className="pill text-[9px] border-amber-400/40 bg-amber-400/10 text-amber-300" title={(u.missing_fields || []).join(", ")}>
                          Incompleto
                        </span>
                      )}
                      {u.inactive && <span className="pill text-[9px] border-orange-400/40 bg-orange-400/10 text-orange-300">Inactivo</span>}
                      {u.unverified_pro && <span className="pill text-[9px] border-red-400/40 bg-red-400/10 text-red-300">Sin verificar</span>}
                      {(u.role === "lawyer" || u.role === "mediator") && !u.banking_info_complete && (
                        <span className="pill text-[9px] border-cyan-400/40 bg-cyan-400/10 text-cyan-300">Sin banking</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Link
                      to={`/app/admin/profesionales?openUserId=${u.id}`}
                      className="btn-outline text-[11px] inline-flex items-center gap-1 px-2 py-1"
                      data-testid={`mant-edit-${u.id}`}
                      title="Editar en Gestión de Profesionales"
                    >
                      <Pencil size={11}/> Editar
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal confirmación de eliminación */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setConfirmOpen(false)} data-testid="mant-confirm-modal">
          <div className="panel p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-2 text-red-300 text-xs uppercase tracking-wider mb-2">
                  <AlertTriangle size={14}/> Acción irreversible
                </div>
                <h2 className="serif text-2xl">Eliminar {selected.size} usuario{selected.size === 1 ? "" : "s"}</h2>
              </div>
              <button onClick={() => setConfirmOpen(false)} className="btn-ghost text-xs py-1.5 px-2" disabled={deleting}>
                <X size={14}/>
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm">
              <p className="text-muted-foreground">
                Estás a punto de eliminar permanentemente <strong className="text-red-200">{selected.size}</strong> cuenta{selected.size === 1 ? "" : "s"} de la colección <code>users</code>.
              </p>
              <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
                <li>No podrás recuperar estas cuentas después de la eliminación.</li>
                <li>Los documentos y transacciones asociados permanecerán en la base para preservar la auditoría.</li>
                <li>La acción quedará registrada en el audit log con tu email y motivo.</li>
                <li>Las cuentas admin y tu propia cuenta serán rechazadas automáticamente.</li>
              </ul>

              <label className="block mt-4">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Motivo (opcional pero recomendado)</span>
                <textarea
                  rows={2}
                  className="field mt-1 w-full"
                  placeholder="Ej: limpieza de cuentas de prueba, duplicados, etc."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="mant-confirm-reason"
                  disabled={deleting}
                />
              </label>

              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setConfirmOpen(false)} className="btn-ghost text-sm" disabled={deleting} data-testid="mant-confirm-cancel">
                  Cancelar
                </button>
                <button
                  onClick={doDelete}
                  disabled={deleting}
                  className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-60 border border-red-600"
                  data-testid="mant-confirm-delete"
                >
                  {deleting ? <Loader2 size={13} className="animate-spin"/> : <Trash2 size={13}/>}
                  {deleting ? "Eliminando…" : `Sí, eliminar ${selected.size}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="panel p-3">
      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider ${color}`}>
        {icon} {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
